import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  TableContainer,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  Chip,
} from "@mui/material";
import { Done as TipIcon, Edit as EditIcon } from "@mui/icons-material";
import moment from "moment";

import Toast from "../../components/toast";
import { minutesToTime } from "../../constants/timeUtils";
import InsuranceUpload from "../../components/insuranceUpload";
import ReportUpload from "../../components/reportUpload";

import { useAppContext } from "../../context/AppContext";
import PdfIcon from "../../assets/icons/pdf-icon.svg";
import TipIconNew from "../../assets/icons/tip-icon.svg";
import AppointmentCalenderIcon from "../../assets/icons/appointment-calender-icon.svg";

import { GetAppointmentDetails } from "../../helpers/Appointment";
import { downloadMedicalFormByChatId } from "../../helpers/Downloads";
import KeyboardBackspaceOutlinedIcon from "@mui/icons-material/KeyboardBackspaceOutlined";
import {
  createVideoSessionInvite,
  listVideoSessionInvites,
  revokeVideoSessionInvite,
} from "../../helpers/VideoSessions";

const UpcomingAppointmentDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const appointmentId =
    location.state?.appointmentId ||
    new URLSearchParams(location.search).get("appointmentId");
  const { userData, isMobile } = useAppContext();

  const [isLoading, setIsLoading] = useState(false);

  const [appointmentData, setAppointmentData] = useState([]);
  const [progress, setProgress] = useState(0);

  const [openReportUpload, setOpenReportUpload] = useState(false);
  const [openInsuranceUpload, setOpenInsuranceUpload] = useState(false);
  const [selectedInsuranceType, setSelectedInsuranceType] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invites, setInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({
    email: "",
  });

  const GetAllAppointmenthanlder = async (_appointmentId) => {
    setIsLoading(true);
    const response = await GetAppointmentDetails(_appointmentId);
    if (response.statusCode === 200 && response.isSuccess === true) {
      const data = response.data;
      if (data) {
        let _progress = 80;
        if (
          data.primaryInsuranceRequired === true &&
          data.secondaryInsuranceRequired === true
        ) {
          _progress += data.primaryInsuranceUploadStatus === true ? 5 : 0;
          _progress += data.secondaryInsuranceUploadStatus === true ? 5 : 0;
        } else if (data.primaryInsuranceRequired === true) {
          _progress += data.primaryInsuranceUploadStatus === true ? 10 : 0;
        } else {
          _progress += 10;
        }
        _progress += data.reportUploadStatus === true ? 10 : 0;
        setProgress(_progress);
      }
      setAppointmentData(data);
    } else {
      Toast.error(response.message);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (appointmentId) {
      GetAllAppointmenthanlder(appointmentId);
    }
  }, [appointmentId]);

  const loadInvites = async () => {
    if (!appointmentId) return;
    const response = await listVideoSessionInvites(appointmentId);
    if (response?.isSuccess) {
      setInvites(response.data || []);
    }
  };

  useEffect(() => {
    loadInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  const handleCreateInvite = async () => {
    const response = await createVideoSessionInvite(appointmentId, inviteForm);
    if (response?.isSuccess) {
      if (response.data?.emailSent === false) {
        Toast.error(
          "The invitation was created, but email delivery failed. Ask the physician to resend it.",
        );
      } else {
        Toast.success("Invite sent.");
        setInviteOpen(false);
        setInviteForm({ email: "" });
      }
      loadInvites();
    } else {
      Toast.error(response?.message || "Unable to send invite.");
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    const response = await revokeVideoSessionInvite(appointmentId, inviteId);
    if (response?.isSuccess) {
      Toast.success("Invite revoked.");
      loadInvites();
    } else {
      Toast.error(response?.message || "Unable to revoke invite.");
    }
  };

  const handleEditClick = () => {
    navigate("/review-patient-form", {
      state: { chatId: appointmentData.chatId },
    });
  };

  const handleUploadInsurance = async (isUploaded) => {
    if (isUploaded) {
      setOpenInsuranceUpload(false);
      GetAllAppointmenthanlder(appointmentId);
    } else {
      Toast.error("Unable to Upload insurance.");
    }
  };

  const handleUploadReport = async (isUploaded) => {
    if (isUploaded) {
      setOpenReportUpload(false);
      GetAllAppointmenthanlder(appointmentId);
    } else {
      Toast.error("Unable to Upload Report.");
    }
  };

  const handleViewMedicalForm = async (formType) => {
    downloadMedicalFormByChatId(appointmentData.chatId, formType);
  };

  const styles = {
    tableCell: {
      color: "#6B6B6B",
    },
    contentBox: {
      p: 2,
      my: 2,
      borderRadius: 2,
      border: "1px solid #e0e0e0",
      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
      backgroundColor: "#ffffff",
    },
    contentBoxInner: {
      display: "flex",
      gap: 1,
      flexWrap: "wrap",
      width: "300px",
      my: 3,
      p: 2,
      borderRadius: { xs: "12px", sm: "12px" },
      boxShadow: "0 5px 10px rgba(0,0,0,0.3)",
    },
  };

  return (
    <>
      <Typography
        variant="body2"
        color="primary"
        mb={2}
        sx={{
          textDecoration: "underline",
          cursor: "pointer",
        }}
        onClick={() => navigate(-1)}
      >
        <KeyboardBackspaceOutlinedIcon
          fontSize="small"
          sx={{ verticalAlign: "middle", mr: 0.5, fontWeight: "medium" }}
        />
        Back
      </Typography>
      <Box
        style={{
          width: "100%",
          border: "1px solid #E8E8E8",
          borderRadius: "12px",
          padding: "10px 15px",
          backgroundColor: "#fff",
        }}
      >
        <ReportUpload
          open={openReportUpload}
          handleClose={() => setOpenReportUpload(false)}
          handleUploadReport={handleUploadReport}
          chatId={appointmentData?.chatId || null}
        />
        <InsuranceUpload
          open={openInsuranceUpload}
          handleClose={() => setOpenInsuranceUpload(false)}
          handleUpload={handleUploadInsurance}
          chatId={appointmentData?.chatId || null}
          currentMessageKey={selectedInsuranceType}
        />
        <Box sx={{ my: 1 }}>
          <Typography fontSize={"24px"} className="sf-medium sf-medium--dark">
            Upcoming Appointment
          </Typography>
        </Box>
        <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Invite family member</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={inviteForm.email}
              onChange={(event) =>
                setInviteForm((current) => ({
                  ...current,
                  email: event.target.value,
                }))
              }
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateInvite}>
            Send Invite
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ my: 1 }}>
          <Typography
            fontSize={"18px"}
            className="sf-regular sf-regular--light-black"
          >
            Complete all sections to generate your Georgia PME PDF and schedule
            your telemedicine visit.
          </Typography>
        </Box>
        <Box sx={styles.contentBox}>
          <Typography
            fontSize={"16px"}
            sx={{ mb: 1 }}
            className="sf-bold sf-bold--dark"
          >
            Your Exam Progress
          </Typography>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 5,
              backgroundColor: "#e6e0f8",
              "& .MuiLinearProgress-bar": {
                borderRadius: 5,
                backgroundColor: "#6C63FF",
              },
            }}
          />

          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
            Progress: {progress}%
          </Typography>
        </Box>
        {progress < 100 && (
          <Box sx={styles.contentBox}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box sx={{ width: "85%" }}>
                <Typography
                  fontSize={"16px"}
                  sx={{ mb: 1 }}
                  className="sf-bold sf-bold--dark"
                >
                  Helpful Tips
                </Typography>
                <Box>
                  {[
                    "Have your medication list ready (including supplements).",
                    "When uploading documents, ensure that all images are clear, well-lit, and in focus so that text and important details remain easily readable without distortion or blur.",
                  ].map((row) => (
                    <Box
                      sx={{ display: "flex", alignItems: "center", my: "2px" }}
                    >
                      <Typography>
                        <TipIcon
                          sx={{ marginRight: "5px", fontSize: 16 }}
                          className="color-primary"
                        />
                        {row}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box
                sx={{
                  width: "15%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {/* <TipIcon
                sx={{ marginRight: "5px", fontSize: 46 }}
                className="color-primary"
              /> */}
                <img src={TipIconNew} width={"50px"} height={"50px"} />
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            {progress < 100 ? (
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={styles.contentBox}>
                  <Typography
                    fontSize={"16px"}
                    sx={{ mb: 1 }}
                    className="sf-bold sf-bold--dark"
                  >
                    Your Exam Progress
                  </Typography>
                  <Typography
                    fontSize={"14px"}
                    sx={{ mb: 1 }}
                    className="sf-regular sf-regular--dark"
                  >
                    You can save and return anytime.
                  </Typography>
                  {appointmentData?.reportUploadStatus === false && (
                    <Box className="background-light" sx={styles.contentBoxInner}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <img
                          src={PdfIcon}
                          width={"36px"}
                          height={"36px"}
                          style={{ marginRight: "5px" }}
                        />
                        <Box>
                          <Typography
                            sx={{ fontSize: "18px" }}
                            className="sf-bold sf-bold--primary"
                          >
                            Upload Your Reports
                          </Typography>
                          <Typography
                            sx={{ fontSize: "12px" }}
                            className="sf-regular sf-regular--dark"
                          >
                            Document
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        className="sf-medium sf-medium--light background-primary"
                        sx={{
                          width: "100%",
                          borderColor: "#8b5cf6",
                          textTransform: "none",
                          borderRadius: "8px",
                          padding: "5px 20px",
                          cursor: "pointer",
                        }}
                        onClick={() => setOpenReportUpload(true)}
                      >
                        Upload Now
                      </Button>
                    </Box>
                  )}
                  {appointmentData?.primaryInsuranceRequired === true &&
                    appointmentData?.primaryInsuranceUploadStatus === false && (
                      <Box
                        className="background-light"
                        sx={styles.contentBoxInner}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={PdfIcon}
                            width={"36px"}
                            height={"36px"}
                            style={{ marginRight: "5px" }}
                          />
                          <Box>
                            <Typography
                              sx={{ fontSize: "18px" }}
                              className="sf-bold sf-bold--primary"
                            >
                              Upload Insurance Card
                            </Typography>
                            <Typography
                              sx={{ fontSize: "12px" }}
                              className="sf-regular sf-regular--dark"
                            >
                              Document
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          className="sf-medium sf-medium--light background-primary"
                          sx={{
                            width: "100%",
                            borderColor: "#8b5cf6",
                            textTransform: "none",
                            borderRadius: "8px",
                            padding: "5px 20px",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setSelectedInsuranceType("PRIMARY_INSURANCE_CARD");
                            setOpenInsuranceUpload(true);
                          }}
                        >
                          Upload Now
                        </Button>
                      </Box>
                    )}
                  {appointmentData.secondaryInsuranceRequired === true &&
                    appointmentData.secondaryInsuranceUploadStatus === false && (
                      <Box
                        className="background-light"
                        sx={styles.contentBoxInner}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={PdfIcon}
                            width={"36px"}
                            height={"36px"}
                            style={{ marginRight: "5px" }}
                          />
                          <Box>
                            <Typography
                              sx={{ fontSize: "18px" }}
                              className="sf-bold sf-bold--primary"
                            >
                              Upload Secondary Insurance Card
                            </Typography>
                            <Typography
                              sx={{ fontSize: "12px" }}
                              className="sf-regular sf-regular--dark"
                            >
                              Document
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          className="sf-medium sf-medium--light background-primary"
                          sx={{
                            width: "100%",
                            borderColor: "#8b5cf6",
                            textTransform: "none",
                            borderRadius: "8px",
                            padding: "5px 20px",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setSelectedInsuranceType("SECONDARY_INSURANCE_CARD");
                            setOpenInsuranceUpload(true);
                          }}
                        >
                          Upload Now
                        </Button>
                      </Box>
                    )}
                </Box>
              </Grid>
            ) : (
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={styles.contentBox}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ width: "80%" }}>
                      <Typography
                        fontSize={"16px"}
                        sx={{ mb: 1 }}
                        className="sf-bold sf-bold--dark"
                      >
                        Appointment Details
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                          fontSize={"14px"}
                          sx={{ mb: 1 }}
                          className="sf-regular sf-regular--dark"
                        >
                          Date:&nbsp;
                        </Typography>
                        <Typography
                          fontSize={"14px"}
                          sx={{ mb: 1 }}
                          className="sf-regular sf-regular--primary"
                        >
                          {appointmentData.appointmentDate
                            ? moment(appointmentData.appointmentDate).format(
                              "MMMM Do, YYYY",
                            )
                            : "N/A"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                          fontSize={"14px"}
                          sx={{ mb: 1 }}
                          className="sf-regular sf-regular--dark"
                        >
                          Time:&nbsp;
                        </Typography>
                        <Typography
                          fontSize={"14px"}
                          sx={{ mb: 1 }}
                          className="sf-regular sf-regular--primary"
                        >
                          {appointmentData.startMinute &&
                            appointmentData.endMinute
                            ? `${minutesToTime(appointmentData.startMinute)} - ${minutesToTime(appointmentData.endMinute)}`
                            : ""}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: "20%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={AppointmentCalenderIcon}
                        width={"50px"}
                        height={"50px"}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <Grid container spacing={1}>
                {progress < 100 && (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={styles.contentBox}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ width: "80%" }}>
                          <Typography
                            fontSize={"16px"}
                            sx={{ mb: 1 }}
                            className="sf-bold sf-bold--dark"
                          >
                            Appointment Details
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              fontSize={"14px"}
                              sx={{ mb: 1 }}
                              className="sf-regular sf-regular--dark"
                            >
                              Date:&nbsp;
                            </Typography>
                            <Typography
                              fontSize={"14px"}
                              sx={{ mb: 1 }}
                              className="sf-regular sf-regular--primary"
                            >
                              {appointmentData.appointmentDate
                                ? moment(appointmentData.appointmentDate).format(
                                  "MMMM Do, YYYY",
                                )
                                : "N/A"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              fontSize={"14px"}
                              sx={{ mb: 1 }}
                              className="sf-regular sf-regular--dark"
                            >
                              Time:&nbsp;
                            </Typography>
                            <Typography
                              fontSize={"14px"}
                              sx={{ mb: 1 }}
                              className="sf-regular sf-regular--primary"
                            >
                              {appointmentData.startMinute &&
                                appointmentData.endMinute
                                ? `${minutesToTime(appointmentData.startMinute)} - ${minutesToTime(appointmentData.endMinute)}`
                                : ""}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            width: "20%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={AppointmentCalenderIcon}
                            width={"50px"}
                            height={"50px"}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                <Box sx={styles.contentBox}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      fontSize={"16px"}
                      className="sf-bold sf-bold--dark"
                    >
                      Family Invite
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setInviteOpen(true)}
                    >
                      Invite family member
                    </Button>
                  </Stack>
                  {invites.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No family members invited yet.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {invites.map((invite) => (
                        <Stack
                          key={invite.id}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Box>
                            <Typography fontSize={14} fontWeight={700}>
                              {invite.guestName || "Family member"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {invite.email || invite.maskedEmail}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label={invite.status} />
                            {invite.status === "pending" && (
                              <Button
                                size="small"
                                color="error"
                                onClick={() => handleRevokeInvite(invite.id)}
                              >
                                Revoke
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                  <Box sx={styles.contentBox}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        fontSize={"16px"}
                        sx={{ mb: 1 }}
                        className="sf-bold sf-bold--dark"
                      >
                        Meidcal Forms
                      </Typography>
                      <Button
                        startIcon={<EditIcon sx={{ fontSize: 18 }} />}
                        variant="outlined"
                        size="small"
                        className="sf-medium sf-medium--primary"
                        onClick={handleEditClick}
                      >
                        Edit & Review Form
                      </Button>
                    </Box>
                    <Typography
                      fontSize={"14px"}
                      sx={{ mb: 1 }}
                      className="sf-regular sf-regular--dark"
                    >
                      {`Review the patient's submitted ${appointmentData?.evaluationType === "PME & POLST" ? "PME and POLST" : "PME"} documentation.`}
                    </Typography>

                    <Box
                      className="background-light-fcfcfc"
                      sx={styles.contentBoxInner}
                    >
                      <Box>
                        <Typography
                          sx={{ fontSize: "18px" }}
                          className="sf-medium sf-medium--primary"
                        >
                          PME Medical Form
                        </Typography>
                        <Typography
                          sx={{ fontSize: "10px" }}
                          className="sf-regular sf-regular--dark"
                        >
                          {`Generated on: ${appointmentData.createdAt ? moment(appointmentData.createdAt).format("Do MMM YYYY") : "N/A"}`}
                        </Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        className="sf-medium sf-medium--primary background-white"
                        sx={{
                          width: "100%",
                          borderColor: "#8b5cf6",
                          textTransform: "none",
                          borderRadius: "8px",
                          padding: "5px 20px",
                          cursor: "pointer",
                        }}
                        onClick={() => handleViewMedicalForm("PME")}
                      >
                        Download
                      </Button>
                    </Box>
                    {appointmentData?.evaluationType === "PME & POLST" && (
                      <Box
                        className="background-light-fcfcfc"
                        sx={styles.contentBoxInner}
                      >
                        <Box>
                          <Typography
                            sx={{ fontSize: "18px" }}
                            className="sf-medium sf-medium--primary"
                          >
                            POLST Medical Form
                          </Typography>
                          <Typography
                            sx={{ fontSize: "10px" }}
                            className="sf-regular sf-regular--dark"
                          >
                            {`Generated on: ${appointmentData.createdAt ? moment(appointmentData.createdAt).format("Do MMM YYYY") : "N/A"}`}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          className="sf-medium sf-medium--primary background-white"
                          sx={{
                            width: "100%",
                            borderColor: "#8b5cf6",
                            textTransform: "none",
                            borderRadius: "8px",
                            padding: "5px 20px",
                            cursor: "pointer",
                          }}
                          onClick={() => handleViewMedicalForm("POLST")}
                        >
                          Download
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default UpcomingAppointmentDetail;
